import React, { useState, useContext, useCallback, useEffect, useRef } from 'react';
import styles from '../Style/ValidationPanel.module.scss';
import { Tabs, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import SateliteTab from './SateliteTab';
import { DatasetRecord } from '../Store/dataTypes';
import { MarkerItem } from '../../../typings/home';
import InSituTab from './InSituTab';
import { StoreContext } from '../../../store/store';
import pointsWithinPolygon from '@turf/points-within-polygon';
import { featureCollection, point, polygon } from '@turf/helpers'
import { Position } from 'deck.gl';

const { TabPane } = Tabs;

interface Props {
    isValidationVisible: boolean;
    setIsValidationVisible: (item: boolean) => void;
    activePointMetadata?: DatasetRecord,
    activePoint?: MarkerItem,
    activePlotValidation: string[],
    setDatasetHeadline: React.Dispatch<React.SetStateAction<string>>,
    setActivePlotValidation: React.Dispatch<React.SetStateAction<string[]>>,
    onChangePointLocation: (coords: number[], forceChange?: boolean) => void,
}

const ValidationPanel: React.FC<Props> = ({
    isValidationVisible,
    setIsValidationVisible,
    activePointMetadata,
    activePoint,
    activePlotValidation,
    setDatasetHeadline,
    setActivePlotValidation,
    onChangePointLocation,
}) => {
    const [activeTab, setActiveTab] = useState("satelite");
    const [disabledChart, setDisableChart] = useState(false)
    const [isInsituAvailable, setIsInsituAvailable] = useState(false)
    const context = useContext(StoreContext)
    const panelClass = isValidationVisible ? styles.validationPanel : styles.hiddenValidationPanel;
    const selectedDatazone = context.datasetZones && context.datasetZones.find((item) => item.id === activePointMetadata?.metadata?.DatasetId)

    const onCloseValidationPanel = () => {
        setIsValidationVisible(false)
    }

    const isValidInsitu = useCallback(() => {
        if (context.showInSituPoints) {
            const tolerance = 0.000001;
            if (selectedDatazone) {
                const selectedPolygon = polygon([selectedDatazone.coordinates as Position[]]);
                const points = featureCollection(context.inSituPoints.map(({ position }) => point(position)));
                const pointsInside = pointsWithinPolygon(points, selectedPolygon);
                if (pointsInside.features.length > 0) {
                    setIsInsituAvailable(true)
                } else {
                    setIsInsituAvailable(false)
                }
            } else {
                setIsInsituAvailable(false)
            }
            const findInSitu = context.inSituPoints.find((item) => {
                if (activePoint?.coordinates) {
                    const itemX = item.position[0];
                    const itemY = item.position[1];
                    const activeX = activePoint.coordinates[0];
                    const activeY = activePoint.coordinates[1];

                    const isXCloseEnough = Math.abs(itemX - activeX) < tolerance;
                    const isYCloseEnough = Math.abs(itemY - activeY) < tolerance;

                    if (isXCloseEnough && isYCloseEnough && isValidationVisible) {
                        return true;
                    }
                }
                return false;
            });
            if (findInSitu) {
                setDisableChart(true)
            } else {
                setDisableChart(false)
            }
        }

    }, [context.inSituPoints, activePoint?.coordinates, context.showInSituPoints, isValidationVisible, activePointMetadata?.metadata?.DatasetId])

    useEffect(() => {
        if(activePointMetadata?.metadata && activePointMetadata?.userGroupPermissions){
            context.setCloseValidation(false)
        }
    }, [activePointMetadata?.metadata, activePointMetadata?.userGroupPermissions])


    useEffect(() => {
        isValidInsitu();
    }, [activePoint?.coordinates, context.inSituPoints, context.showInSituPoints, isValidationVisible, activePointMetadata?.metadata?.DatasetId]);

    return (
        <div className={panelClass}>
            <div className={styles.headerSection}>
                <Typography.Title level={4} className={styles.customTitle}>
                    Validation
                </Typography.Title>
                <Tabs className={styles.customTabsHeader} activeKey={activeTab} onChange={(key) => setActiveTab(key)}>
                    <TabPane
                        tab={(
                            <Typography.Text>
                                Satellite
                            </Typography.Text>
                        )}
                        key="satelite" />
                    <TabPane
                        tab={(
                            <Typography.Text>
                                In Situ
                            </Typography.Text>
                        )}
                        key="inSitu" />
                </Tabs>
                <CloseOutlined className={styles.closeIcon} onClick={onCloseValidationPanel} />
            </div>
            <div className={styles.contentSection}>
                {activeTab === "inSitu" && !context.closeValidation && (
                    <InSituTab
                        activePoint={activePoint}
                        activePointMetadata={activePointMetadata}
                        validInSituPoint={disabledChart}
                        isInsituAvailable={isInsituAvailable}
                        onChangePointLocation={onChangePointLocation}
                    />
                )}
                {activeTab === "satelite" && !context.closeValidation && (
                    <SateliteTab
                        downloadItems={activePoint?.downloadPage!}
                        coordinates={activePoint?.coordinates!}
                        previewDatasetId={activePointMetadata?.metadata?.DatasetId ?? ''}
                        userGroupPermissions={activePointMetadata?.userGroupPermissions!}
                        pointName={activePoint?.label ?? ''}
                        gridCellSize={activePointMetadata?.cellSize?.toString()!}
                        waterDepth={activePointMetadata?.depth?.toString()!}
                        setDatasetHeadline={setDatasetHeadline}
                        activePlot={activePlotValidation}
                        setActivePlot={setActivePlotValidation}
                    />
                )}
            </div>
        </div>
    );
};

export default ValidationPanel;
